/**
 * The painted card faces (Part 09 mockup `NC.task`, `NC.memory`): a task
 * and a thing Denker learned, as their wall cards show them. The wall's new
 * cards and the pockets they open render these same faces, so the opened
 * pocket's header is the card it came from (Part 10 §4). Every size is in
 * em: the host sets the font size.
 */
import type { ReactNode } from "react";
import type { ThingKind } from "@/api/memory-focus";
import type { TaskStatus } from "@/api/tasks";
import {
  STATUS_DOT_COLORS,
  TASK_STATUS_LABEL_KEYS,
} from "@/components/shapes/taskboard-frame/task-metadata-chips";
import { useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { cn } from "@/lib/cn";
import { kindName } from "@/lib/memory-thing-kinds";

/** A name this long or shorter takes the large type (mockup `.d3`). */
const SHORT_NAME = 22;
const FACE_CLASS = "px-[1.45em] pb-[1.5em] pt-[1.4em]";
const EYEBROW_CLASS = "text-[0.74em] font-medium tracking-[0.05em]";
const NAME_CLASS =
  "mt-[0.5em] font-display text-[1.45em] font-medium leading-[1.16] tracking-[-0.025em]";

/** A control on the task paint: its soft ink, the chip tint on hover. */
export const TASK_PAINT_BUTTON_CLASS =
  "text-[color:var(--card-paint-task-soft)] hover:bg-[var(--card-paint-task-chip)] hover:text-[color:var(--card-paint-task-ink)] focus-visible:bg-[var(--card-paint-task-chip)] focus-visible:text-[color:var(--card-paint-task-ink)]";

/** The memory node glyph (mockup `NODE_GLYPH`): a centre and three links. */
function NodeGlyph() {
  return (
    <svg
      className="h-[2.1em] w-[2.1em] shrink-0"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <g stroke="currentColor" strokeOpacity=".45" strokeWidth="1.4">
        <path d="M12 12 5 6M12 12l7-3M12 12l-2 7" />
      </g>
      <g fill="currentColor">
        <circle cx="12" cy="12" r="3.2" />
        <circle cx="5" cy="6" r="1.9" fillOpacity=".6" />
        <circle cx="19" cy="9" r="1.9" fillOpacity=".6" />
        <circle cx="10" cy="19" r="1.9" fillOpacity=".6" />
      </g>
    </svg>
  );
}

/**
 * A task on the task paint: the eyebrow with its ticket (and the host's
 * controls), the title in display type, then the status chip and the
 * assignee. The host passes the title so it may clamp or edit it.
 */
export function TaskCardFace({
  eyebrow,
  ticket,
  title,
  status,
  assigneeName,
  actions,
  className,
  testId,
  testIds,
}: {
  eyebrow: string;
  ticket: string | null;
  title: ReactNode;
  status: TaskStatus;
  assigneeName: string | null;
  /** Controls at the end of the eyebrow row, after the ticket. */
  actions?: ReactNode;
  className?: string;
  testId?: string;
  testIds: { ticket: string; status: string };
}) {
  const tTaskboard = useT(framesTaskboardMessages);
  return (
    <div
      className={cn(
        FACE_CLASS,
        "text-[color:var(--card-paint-task-ink)]",
        className,
      )}
      style={{
        background: "var(--card-paint-task)",
        boxShadow: "var(--card-highlight)",
      }}
      data-testid={testId}
    >
      <div className="flex min-h-[1.9em] items-center gap-[0.6em] text-[color:var(--card-paint-task-soft)]">
        <span className={EYEBROW_CLASS}>{eyebrow}</span>
        {ticket ? (
          <span
            className="ml-auto text-[0.8em] font-medium tabular-nums tracking-[0.02em]"
            data-testid={testIds.ticket}
          >
            {ticket}
          </span>
        ) : null}
        {actions ? (
          <div
            className={cn(
              "-my-[0.4em] -mr-[0.7em] flex items-center",
              !ticket && "ml-auto",
            )}
          >
            {actions}
          </div>
        ) : null}
      </div>
      <div className={NAME_CLASS}>{title}</div>
      <div className="mt-[1em] flex flex-wrap items-center gap-x-[0.7em] gap-y-[0.3em] text-[0.8em] text-[color:var(--card-paint-task-soft)]">
        <span
          className="inline-flex h-[1.9em] items-center gap-[0.45em] whitespace-nowrap rounded-pill bg-[var(--card-paint-task-chip)] pl-[0.65em] pr-[0.75em] font-medium text-[color:var(--card-paint-task-ink)]"
          data-testid={testIds.status}
        >
          <i
            className="h-[7px] w-[7px] shrink-0 rounded-full"
            style={{ background: STATUS_DOT_COLORS[status] }}
          />
          {tTaskboard(TASK_STATUS_LABEL_KEYS[status])}
        </span>
        {assigneeName ? (
          <span className="min-w-0 truncate">{assigneeName}</span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A thing Denker learned, on the memory paint: the node glyph, "Denker
 * learned" and the kind chip, the name (large when short) and, when given,
 * the description. The wall card clamps them; the pocket shows them whole.
 */
export function ThingCardFace({
  name,
  kind,
  description,
  clamp = true,
  className,
  testId,
}: {
  name: string;
  kind: ThingKind;
  description?: string;
  /** Cuts the name at three lines and the description at four. */
  clamp?: boolean;
  className?: string;
  testId?: string;
}) {
  const t = useT(framesSharedMessages);
  const short = name.length <= SHORT_NAME;
  return (
    <div
      className={cn(
        FACE_CLASS,
        "text-[color:var(--card-paint-memory-ink)]",
        className,
      )}
      style={{
        background: "var(--card-paint-memory)",
        boxShadow: "var(--card-highlight)",
      }}
      data-testid={testId}
    >
      <div className="flex min-h-[1.9em] items-center gap-[0.6em]">
        <NodeGlyph />
        <span
          className={cn(
            EYEBROW_CLASS,
            "text-[color:var(--card-paint-memory-soft)]",
          )}
        >
          {t("denkerLearned")}
        </span>
        <span className="ml-auto inline-flex h-[1.9em] items-center whitespace-nowrap rounded-pill bg-[var(--card-paint-memory-chip)] px-[0.8em] text-[0.78em] font-medium">
          {kindName(kind)}
        </span>
      </div>
      <p
        className={cn(
          NAME_CLASS,
          "[overflow-wrap:anywhere]",
          clamp && "line-clamp-3",
          short && "text-[2.1em] leading-[1.08] tracking-[-0.03em]",
        )}
      >
        {name}
      </p>
      {description ? (
        <p
          className={cn(
            "mt-[0.55em] leading-[1.45] text-[color:var(--card-paint-memory-soft)]",
            clamp && "line-clamp-4",
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}
